// src/groups — 컨덕터 코드 DS(그룹) 원본. 데브 레포에는 **레포당 1벌**(src/ui-groups/)로 복사되고 sync 로 오간다(DECISIONS 2026-10-22·23).
//   허용 import = react · @ant-design/icons · dayjs · @musinsa/pbo-core(+/theme) · @musinsa/pbo-assets(아이콘) · 같은 폴더(./). 게이트 = check:groups-portable.
// 표 묶음 그룹(cmp.TableGroup) — 선택 스코프 > [위 슬롯] + 액션바(왼쪽 버튼들 | 페이저 · 목록 검색 · 구분선 · 오른쪽 버튼들) + 표.
//
// 구조(데모 정의 skeletonBuilders buildTableGroup 1:1):
//   TableSelectionScope(key = selectionEpoch)
//     Flex vertical gap 8 align stretch
//       {above}
//       Flex(헤더, padding 4px 0, className pbo-v2-actionbar-sticky, justify = left 있으면 space-between / 없으면 flex-end)
//         Space(왼쪽)  ← title(16/600) + left                                    … left 있을 때만
//         Space(오른쪽) ← [페이저] [목록 검색] [구분선] {right}                      … rightFirst=false
//         Space(오른쪽) ← {right} [구분선] [페이저] [목록 검색]                      … rightFirst=true
//       {table}
// 페인트: `.pbo-v2-actionbar-sticky`(sticky top 0·z 20·흰 배경·아래 덮개 8) 는 pbo-core dist 가 소유. 스티키 타이틀 아래면 DS 가 top 56 으로 올린다.
// 표 헤더 고정 오프셋: 액션바가 실제로 멈추는 위치(computed top) + 48 — 어댑터 TableRender 와 같은 계산(tableStickyOffset.ts). table 을
//   함수로 넘기면 `{ stickyOffset }` 로 받는다. 분할 패널·모달 안은 top 0 이라 48.
// 페이저 규약(어댑터 TablePagerRender 1:1): '몇 개씩' 셀렉트는 paged 기본 숨김(= 10,000개씩, MOMS·MWMS 2026-08-24), range 는 1만 건 이상이면 숨김.
//   숨겨졌을 때 한 페이지 = fixedPageSize(기본 10,000). 화면은 pagerWindow() 로 같은 값을 계산해 행을 자른다 — 두 벌이면 어긋난다.
// 선택: 스코프 안 표의 체크를 DS 훅이 세고(SelStat·SelGatedButton), onSelect 로 선택 행을 화면에 흘린다(SelBridge — 선택이 있는 스코프만 보고,
//   푼 신호는 빈 배열 + scope). 선택 비우기는 selectionEpoch 를 올린다(스코프 재마운트 — 조회·페이지 이동·저장 뒤).
// 2026-10-23 moms-fe 손수정 흡수: columnLayout(표 prop 패스스루라 그룹 밖) · stickyOffset 104(여기서 측정) · 페이저 clamp(pagerWindow) ·
//   EditSelTable 13화면 동일 본문(여기 한 벌) · selResetToken/selReset(selectionEpoch) · SelGatedButton exact·busy.
//   그룹 밖에 남는 것: 가상 스크롤(표 prop) · confirmOnce(확인창 모듈 함수) · 이동 전 canChangePage(화면 상태 보호).
import { type ComponentProps, Fragment, type Key, type ReactElement, type ReactNode, cloneElement, isValidElement, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { core } from '@musinsa/pbo-core'
import { resolveTableStickyOffset, STICKY_OFFSET_ACTIONBAR } from './tableStickyOffset'

// ── 페이저 ────────────────────────────────────────────────────────────────────────────

export type TablePagerState = { page: number; size: number }
export type TableGroupPager = {
  /** 전체 건수(조회 결과). 목록 검색이 걸리면 화면이 걸러진 건수를 넘긴다. */
  total: number
  /** 페이지 상태 — 화면이 소유한다(표 조회·URL 복원이 같은 값을 읽는다). */
  value: TablePagerState
  onChange: (next: TablePagerState) => void
  /** 'paged' = 'n / N 페이지'(기본) · 'range' = '총 N개 중 x-y'. */
  variant?: 'paged' | 'range'
  /** '몇 개씩' 셀렉트 — 'auto' = 1만 건 이상이면 숨김 · 'hide' · 'show'. 기본: paged 는 'hide', range 는 'auto'. */
  sizeChanger?: 'auto' | 'hide' | 'show'
  pageSizeOptions?: string[]
  /** 셀렉트가 숨겨졌을 때 한 페이지 크기. 기본 10,000. */
  fixedPageSize?: number
}

/** 실제 적용되는 페이지 창 — 셀렉트 숨김이면 size 는 fixedPageSize, page 는 전체 건수 안으로 자른다(clamp). 그룹과 화면이 같은 함수를 쓴다. */
export function pagerWindow(p: TableGroupPager): TablePagerState {
  const variant = p.variant ?? 'paged'
  const sizeChanger = p.sizeChanger ?? (variant === 'paged' ? 'hide' : 'auto')
  const hidden = sizeChanger === 'hide' || (sizeChanger === 'auto' && p.total >= 10000)
  const size = hidden ? (p.fixedPageSize || 10000) : p.value.size
  const last = Math.max(1, Math.ceil(p.total / Math.max(1, size)))
  return { page: Math.min(Math.max(1, p.value.page), last), size }
}

function GroupPager({ p }: { p: TableGroupPager }) {
  const variant = p.variant ?? 'paged'
  const sizeChanger = p.sizeChanger ?? (variant === 'paged' ? 'hide' : 'auto')
  const win = pagerWindow(p)
  return (
    <core.TablePager variant={variant} page={win.page} pageSize={win.size} total={p.total} pageSizeOptions={p.pageSizeOptions}
      showSizeChanger={sizeChanger === 'auto' ? undefined : sizeChanger === 'show'}
      onPage={(page, size) => p.onChange({ page: size !== p.value.size ? 1 : page, size })} />
  )
}

// ── 목록 검색 ───────────────────────────────────────────────────────────────────────────

export type TableGroupListSearch = {
  /** 확정된 검색어 — 화면이 소유한다(표가 같은 값으로 거른다). 확정은 엔터/돋보기(onSearch). */
  value: string
  onSearch: (q: string) => void
  placeholder?: string
  width?: number
}

// ── 선택 헬퍼(스코프 안에서 쓰는 것) ─────────────────────────────────────────────────────

/** 표 카운트 — 같은 스코프 안 표의 선택 개수를 '선택 N / 전체' 로 함께 그린다. 스코프 밖이면 전체만. */
export function SelStat(props: ComponentProps<typeof core.TableStat>) {
  const n = core.useTableSelectionCount()
  return <core.TableStat {...props} selectedCount={n && n > 0 ? n : undefined} />
}

/** 선택 0개면 비활성 — 같은 스코프 안 표의 선택 개수를 읽는다. exact = 정확히 그 개수일 때만 활성(단건 처리). busy = 요청 진행 중 잠금(화면이 넘긴다). */
export function SelGatedButton({ disabled, exact, busy = false, ...rest }: ComponentProps<typeof core.Button> & { exact?: number; busy?: boolean }) {
  const n = core.useTableSelectionCount() ?? 0
  return <core.Button {...rest} disabled={disabled || n === 0 || (exact != null && n !== exact) || busy} />
}

/**
 * 편집 시 자동 체크 표 — 셀을 고친 행을 체크 선택에 더한다(1.0 CommonUtil.onCellValueChanged 의 setSelected(true) 미러).
 * core.Table 을 그대로 감싼다(프롭 동일). 사람이 바꾼 선택은 그대로 따르고, 상위가 selectedRowKeys 를 주면 그 값에 더해 onChange 로 올린다.
 * 고친 행 = 받은 dataSource 와도 직전 onRowsChange 와도 다른 행. 행 수가 바뀐 변경(삭제 열)은 편집이 아니라 건너뛴다.
 * 연쇄 편집: 배선이 onRowsChange 에서 다른 행까지 바꿔 되먹이면(상위 허용여부 N → 하위 전파) 그 행도 체크에 더한다 — 편집 직전 dataSource 와
 *  그 뒤 처음 되먹인 dataSource 를 견줘 바뀐 행. 되먹임 확인 = 사람이 고친 행이 새 dataSource 에서 DS 가 돌려준 값과 같다.
 * 선택은 스코프 key(selectionEpoch)가 바뀌어 다시 마운트될 때 비워진다. moms-fe 13화면에 같은 본문이 복사돼 있던 것(653a5e75)을 한 벌로.
 * editGate(표 dev.editRules): 고친 결과가 규칙에 걸리면 반영·자동 체크를 멈추고 확인창을 연다. [확인] 뒤 가드가 apply 로 돌아와 정상 통로를 탄다.
 *  codegen 헬퍼(emitFilterField EditSelTable)와 같은 본문 — 개선(ui-groups) 산출물이 이 사본을 import 한다(2026-10-26 MWMS 배송지역 TS2322: 헬퍼에만 있었다).
 */
export function EditSelTable<T extends Record<string, unknown>>({ rowSelection, onRowsChange, dataSource, editGate, ...rest }: Parameters<typeof core.Table<T>>[0] & { editGate?: (prev: readonly T[], next: T[], apply: (rows: T[]) => void) => boolean }) {
  const [inner, setInner] = useState<Key[]>([])
  const keys = rowSelection?.selectedRowKeys ?? inner
  const cur = useRef(keys)
  cur.current = keys
  const last = useRef<T[] | null>(null)
  const keyField = typeof rest.rowKey === 'string' ? rest.rowKey : 'key'
  const keyOf = (r: T, i: number): Key => (r[keyField] as Key | undefined) ?? i
  // Array.from — es5 타깃(store-admin·mwms)에서 Set 스프레드는 TS2802(코드젠 헬퍼와 같은 꼴, 2026-10-26 MOSS 개선 모드 dry-run).
  const same = (a: T, b: T) => a === b || Array.from(new Set([...Object.keys(a), ...Object.keys(b)])).every((k) => Object.is(a[k], b[k]))
  const index = (rs: readonly T[]) => new Map(rs.map((r, i) => [String(keyOf(r, i)), r]))
  const edited = (next: T[]): Key[] => {
    const source = dataSource ?? []
    if (next.length !== source.length) return []
    const src = index(source)
    const prev = last.current && last.current.length === next.length ? index(last.current) : null
    return next.flatMap((r, i) => {
      const k = keyOf(r, i)
      const a = src.get(String(k))
      if (!a || same(a, r)) return []
      const b = prev?.get(String(k))
      return b && same(b, r) ? [] : [k]
    })
  }
  const addSel = (added: Key[], rows: readonly T[]) => {
    const have = new Set(cur.current.map(String))
    const extra = added.filter((k) => !have.has(String(k)))
    if (!extra.length) return
    const merged = [...cur.current, ...extra]
    const set = new Set(merged.map(String))
    cur.current = merged
    setInner(merged)
    rowSelection?.onChange?.(merged, rows.filter((r, i) => set.has(String(keyOf(r, i)))), { type: 'multiple' })
  }
  const cascade = useRef<{ base: readonly T[]; next: readonly T[] } | null>(null)
  useEffect(() => {
    const p = cascade.current
    if (!p) return
    const source = dataSource ?? []
    if (source.length !== p.base.length) {
      cascade.current = null
      return
    }
    const base = index(p.base)
    const changed = source.flatMap((r, i) => {
      const k = keyOf(r, i)
      const b = base.get(String(k))
      return b && !same(b, r) ? [k] : []
    })
    if (!changed.length) return
    cascade.current = null
    const src = index(source)
    const fed = p.next.every((r, i) => {
      const k = String(keyOf(r, i))
      const b = base.get(k)
      if (!b || same(b, r)) return true
      const s = src.get(k)
      return !!s && same(s, r)
    })
    if (fed) addSel(changed, source)
  })
  /** 정상 통로 — 바뀐 행을 찾아 두고, 상위에 알리고, 그 행을 체크에 더한다. 편집 규칙 [확인] 뒤에도 이 통로로 돌아온다. */
  const commit = (next: T[]) => {
    const added = edited(next)
    last.current = next
    if (next.length === (dataSource ?? []).length) cascade.current = { base: dataSource ?? [], next }
    onRowsChange?.(next)
    if (!added.length) return
    // DS 표는 onRowsChange 를 자기 상태 갱신 함수 안에서 부른다 — 곧장 갱신하면 렌더 중 갱신 경고라 한 박자 미룬다.
    queueMicrotask(() => addSel(added, next))
  }
  return (
    <core.Table<T>
      {...rest}
      dataSource={dataSource}
      rowSelection={rowSelection && {
        ...rowSelection,
        selectedRowKeys: keys,
        onChange: (k, rows, info) => {
          setInner(k)
          cur.current = k
          rowSelection.onChange?.(k, rows, info)
        },
      }}
      onRowsChange={(next) => {
        // 편집 규칙 — 걸리면 반영도 자동 체크도 하지 않는다(되돌림 + 확인창은 가드가). [확인] 뒤엔 가드가 commit 으로 돌아온다.
        if (editGate && editGate(dataSource ?? [], next, commit)) return
        commit(next)
      }}
    />
  )
}

/** 선택 행 브리지 — 스코프 안 표의 선택 행을 화면으로. 선택이 있는 스코프만 보고(위아래 표가 서로 덮지 않게), 푼 신호는 빈 배열 + scope. */
function SelBridge({ scope, onSelect, refill }: { scope: string; onSelect: (rows: Record<string, unknown>[], scope: string) => void; refill?: number }) { // refill: 다른 스코프가 자기 epoch 로 다시 마운트돼 '풀림'([])을 알린 뒤, 이 스코프의 선택을 되알리는 신호(확인창 selResetScope:'own' — moms-fe rgr02 LOGISPRD-14915)
  const s = core.useTableSelection()
  const rows = s?.rows
  const owned = useRef(false)
  const cb = useRef(onSelect)
  cb.current = onSelect
  useEffect(() => {
    if (rows?.length) {
      owned.current = true
      cb.current(rows as Record<string, unknown>[], scope)
    } else if (owned.current) {
      owned.current = false
      cb.current([], scope)
    }
    return () => { // 스코프 재마운트(selectionEpoch)·이탈 때 — 이 스코프가 선택을 쥐고 있었으면 '풀림' 을 알린다(인라인 SelBridge 가 selRef 를 비우던 것과 같은 효과)
      if (!owned.current) return
      owned.current = false
      cb.current([], scope)
    }
  }, [rows, scope, refill]) // refill 은 다른 스코프의 정리가 먼저 돌고 이 효과가 뒤에 돈다
  return null
}

// ── 그룹 본체 ─────────────────────────────────────────────────────────────────────────

export type TableGroupProps = {
  /** 표 — 요소(컴포넌트 요소면 측정한 헤더 고정 오프셋을 `stickyOffset` prop 으로 넣어 준다 · 프래그먼트/DOM 은 그대로) 또는 `({ stickyOffset }) => 노드`.
   *  요소 형태가 기본 — 렌더 prop 은 소비 레포 ESLint(react/no-unstable-nested-components)가 컴포넌트 정의로 본다(2026-10-23). */
  table: ReactNode | ((ctx: { stickyOffset: number }) => ReactNode)
  /** 액션바 위 자리(상태 탭·요약 줄). */
  above?: ReactNode
  /** 왼쪽 제목(16/600) — 왼쪽 자리 맨 앞. */
  title?: string
  /** 왼쪽 자리(카운트·저장/삭제 버튼). 있으면 헤더가 space-between. */
  left?: ReactNode
  /** 오른쪽 자리(엑셀 다운로드 등). */
  right?: ReactNode
  /** true = 오른쪽 버튼들이 페이저·검색보다 앞. */
  rightFirst?: boolean
  /** 페이저 — false 면 없음. */
  pager?: TableGroupPager | false
  /** 목록 검색 — false 면 없음. */
  listSearch?: TableGroupListSearch | false
  /** 선택 비우기 세대 — 올리면 스코프가 다시 마운트돼 체크가 풀린다. 목록 검색 칸도 같이 리셋된다. */
  selectionEpoch?: number
  /** 다른 스코프가 자기 epoch 로 다시 마운트된 신호 — 이 스코프의 선택을 onSelect 로 되알린다(확인창 selResetScope:'own' 화면만). */
  refill?: number
  /** 선택 행 구독 — scope 는 이 그룹 id(위아래 표 구분). onSelect 가 있을 때만 브리지를 단다. */
  scope?: string
  onSelect?: (rows: Record<string, unknown>[], scope: string) => void
  /** 표 헤더 고정 오프셋 기준값. 기본 48(액션바만) — 액션바가 실제로 멈추는 위치를 더해 table 에 넘긴다. 상태 탭 등 손값(102)은 그대로. */
  stickyOffset?: number
}

const Divider = () => <span style={{ height: 18, margin: '0 4px', borderLeft: '1px solid #d9d9d9', display: 'block', flex: 'none' }} />

export function TableGroup({ table, above, title, left, right, rightFirst = false, pager, listSearch, selectionEpoch = 0, refill, scope = 'tableGroup', onSelect, stickyOffset = STICKY_OFFSET_ACTIONBAR }: TableGroupProps) {
  const hasLeft = title != null || left != null
  const hasRight = right != null
  const showPager = pager !== false && pager != null
  const showSearch = listSearch !== false && listSearch != null
  // 액션바가 실제로 멈추는 위치(computed top) — 스티키 타이틀 아래면 DS CSS 가 56 으로 올린다. 표 헤더 오프셋은 그 값 + 48.
  const headerRef = useRef<HTMLDivElement>(null)
  const [barTop, setBarTop] = useState<number | null>(null)
  useLayoutEffect(() => {
    const el = headerRef.current
    if (!el) return
    const read = () => {
      const t = parseFloat(getComputedStyle(el).top)
      setBarTop(Number.isFinite(t) ? t : null)
    }
    read()
    window.addEventListener('resize', read)
    return () => window.removeEventListener('resize', read)
  }, [])
  const effOffset = resolveTableStickyOffset(stickyOffset, barTop) ?? stickyOffset
  const pagerNode = showPager ? <GroupPager p={pager} /> : null
  const searchNode = showSearch ? (
    <core.Input.Search key={selectionEpoch} placeholder={listSearch.placeholder ?? '목록 검색'} allowClear style={{ width: listSearch.width ?? 240 }}
      defaultValue={listSearch.value} onSearch={(v) => listSearch.onSearch((v ?? '').trim())} />
  ) : null
  return (
    <core.TableSelectionScope key={selectionEpoch}>
      {onSelect ? <SelBridge scope={scope} onSelect={onSelect} refill={refill} /> : null}
      <core.Flex vertical gap={8} align="stretch">
        {above ?? null}
        <core.Flex ref={headerRef} style={{ padding: '4px 0px 4px 0px' }} gap={8} justify={hasLeft ? 'space-between' : 'flex-end'} align="center" className="pbo-v2-actionbar-sticky">
          {hasLeft && (
            <core.Space>
              {title != null && <core.Typography.Text style={{ fontSize: 16, fontWeight: 600, lineHeight: '140%', color: '#000000e0', marginRight: 12 }}>{title}</core.Typography.Text>}
              {left}
            </core.Space>
          )}
          {rightFirst ? (
            <core.Space>
              {right}
              <Divider />
              {pagerNode}
              {searchNode}
            </core.Space>
          ) : (
            <core.Space>
              {pagerNode}
              {searchNode}
              {hasRight && (showPager || showSearch) && <Divider />}
              {right}
            </core.Space>
          )}
        </core.Flex>
        {typeof table === 'function'
          ? table({ stickyOffset: effOffset })
          : isValidElement(table) && typeof table.type !== 'string' && table.type !== Fragment
            ? cloneElement(table as ReactElement<{ stickyOffset?: number }>, { stickyOffset: effOffset })
            : table}
      </core.Flex>
    </core.TableSelectionScope>
  )
}
